/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useMemo } from 'react'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { PieChart as PieChartIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import type { ModelDistributionItem } from '../types'

interface DistributionChartProps {
  data: ModelDistributionItem[]
  loading: boolean
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(var(--muted-foreground))',
]

export function DistributionChart({ data, loading }: DistributionChartProps) {
  const { t } = useTranslation()

  const chartData = useMemo(() => {
    if (!data.length) return []
    const total = data.reduce((sum, item) => sum + item.value, 0)
    if (total === 0) return []
    return data.map((item) => ({
      name: item.name,
      value: item.value,
      percent: ((item.value / total) * 100).toFixed(1),
    }))
  }, [data])

  return (
    <div className='overflow-hidden rounded-xl border bg-card/50 backdrop-blur-sm'>
      <div className='flex items-center gap-2 border-b px-4 py-3 sm:px-5 sm:py-4'>
        <PieChartIcon
          className='text-muted-foreground/60 size-4'
          aria-hidden='true'
        />
        <div className='text-sm font-semibold'>{t('API Call Distribution')}</div>
      </div>

      <div className='flex flex-col gap-3 p-3 sm:p-4'>
        <div className='h-[220px] sm:h-[260px]'>
          {loading ? (
            <Skeleton className='h-full w-full' />
          ) : chartData.length === 0 ? (
            <div className='text-muted-foreground flex h-full items-center justify-center text-xs'>
              {t('No data available')}
            </div>
          ) : (
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='50%'
                  innerRadius='55%'
                  outerRadius='85%'
                  paddingAngle={2}
                  dataKey='value'
                  stroke='hsl(var(--background))'
                  strokeWidth={2}
                >
                  {chartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null
                    const item = payload[0].payload as {
                      name: string
                      value: number
                      percent: string
                    }
                    return (
                      <div className='border-border/50 bg-background grid min-w-32 items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl'>
                        <div className='font-medium'>{item.name}</div>
                        <div className='flex items-center gap-2'>
                          <span className='text-muted-foreground'>
                            {t('Quota')}
                          </span>
                          <span className='text-foreground font-mono font-medium tabular-nums'>
                            {item.value}
                          </span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <span className='text-muted-foreground'>
                            {t('Percent')}
                          </span>
                          <span className='text-foreground font-mono font-medium tabular-nums'>
                            {item.percent}%
                          </span>
                        </div>
                      </div>
                    )
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {!loading && chartData.length > 0 && (
          <div className='grid grid-cols-2 gap-2'>
            {chartData.map((item, index) => (
              <div
                key={item.name}
                className='flex items-center gap-2 rounded-lg border px-2.5 py-1.5'
              >
                <div
                  className='size-2 shrink-0 rounded-sm'
                  style={{
                    backgroundColor: COLORS[index % COLORS.length],
                  }}
                />
                <span className='flex-1 truncate text-xs font-medium'>
                  {item.name}
                </span>
                <span className='text-muted-foreground font-mono text-[11px] tabular-nums'>
                  {item.percent}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
